import React, { useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import styled from 'styled-components/macro';
import { colors } from 'src/styles/index';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { getBrandingColor, colorTypes } from 'src/branding';
import Button from './Button';
import { Column, Icon } from './Layout';

export const OKButtonLarge = styled(Button)`
  flex: 1 1 auto;
  margin: -40px;
  padding: 20px 0;
  margin-top: 0;
  border: none;
  background: transparent;
  border-radius: 0;
  color: ${getBrandingColor(colorTypes.linkPrimary)};
`;

export const ModalHeader = styled(Column)`
  flex: 1 1 auto;
  margin: -40px;
  margin-bottom: 0;
  border: none;
  border-top-left-radius: 15px;
  border-top-right-radius: 15px;
  overflow: hidden;
`;

const ModalBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  background: rgba(
    31,
    31,
    31,
    0.7
  ); /*has to use rgba, edge does not support alpha for hex*/
  z-index: 500;
  display: flex;
  align-items: center;
  padding: 2em;
  overflow: hidden;
  max-height: 100%;
`;

const StyledModal = styled(Column.withComponent('dialog'))`
  flex: 0 1 auto;
  display: flex;
  flex-direction: column;
  position: relative;
  height: ${({ height }) => height || 'fit-content'};
  width: ${({ width }) => width || 'fit-content'};
  max-height: calc(100% - 4em);
  margin: auto;
  border: none;
  border-radius: 1.5em;
  padding: 2em;
  /*background: rgba(255, 255, 255, 0.9);*/
  background: white;
  transition: width 0.15s ease-out, height 0.15s ease-out;
  overflow: hidden;

  @media (max-width: 767px) {
    ${({mobileStyle}) => mobileStyle && mobileStyle};
  }
`;

let instancesCount = 0;

const updateBodyScroll = () => {
  const { style: htmlStyle } = document.documentElement;
  const { style: bodyStyle } = document.body;
  if (instancesCount) {
    htmlStyle.overflow = 'hidden';
    bodyStyle.overflow = 'hidden';
  }
  if (htmlStyle.removeProperty) {
    htmlStyle.removeProperty('overflow');
    bodyStyle.removeProperty('overflow');
  } else {
    if (htmlStyle.removeProperty) {
      htmlStyle.removeProperty('overflow');
      bodyStyle.removeProperty('overflow');
    } else {
      htmlStyle.removeAttribute('overflow');
      bodyStyle.removeAttribute('overflow');
    }
  }
};

const CloseModalBtn = styled.button`
  background: none;
  color: gray;
  font-size: 1rem;
  position: absolute;
  right: 16px;
  top: 9px;
  border-radius: 50%;
  border: 1px solid transparent;
  padding: 0;
  height: 1.5em;
  width: 1.5em;
  outline: none;
  display: flex;
  align-items: center;

  svg {
    margin: auto;
    flex: 0 0 auto;
  }

  :hover,
  :focus {
    cursor: pointer;
    border: 1px solid #cfcfcf;
  }
`;

const Modal = ({
  children,
  closable,
  onClose,
  noEscape,
  width,
  height,
  mobileStyle,
  ...props
}) => {
  const wrapperRef = useRef();
  const close = useCallback(() => {
    if (onClose && closable) {
      onClose();
    }
  }, [closable, onClose]);

  useEffect(() => {
    const onKeyUp = (e) => {
      if (e.code === 'Escape' && !noEscape) {
        close();
      }
    };

    instancesCount += 1;
    updateBodyScroll();

    window.addEventListener('keyup', onKeyUp);

    return () => {
      instancesCount -= 1;
      updateBodyScroll();

      window.removeEventListener('keyup', onKeyUp);
    };
  }, [close, noEscape]);

  return createPortal(
    <ModalBackdrop onClick={() => close()} ref={wrapperRef}>
      <StyledModal
        open
        height={height}
        width={width}
        mobileStyle={mobileStyle}
        onClick={(e) => {
          e.stopPropagation();
          return false;
        }}
        {...props}
      >
        {children}
        {closable && (
          <CloseModalBtn aria-label="Lukk modal" onClick={close}>
            <Icon icon={faTimes} />
          </CloseModalBtn>
        )}
      </StyledModal>
    </ModalBackdrop>,
    document.getElementById('modal'),
  );
};

Modal.propTypes = {
  children: PropTypes.node.isRequired,
  closable: PropTypes.bool,
  onClose: PropTypes.func,
};

Modal.defaultProps = {
  closable: false,
  onClose: null,
};

export default Modal;
